import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { useWindowWidth } from '../hooks';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import StockRecord from '../components/StockRecord';
import StockItemForm from '../components/StockItemForm';
import StockUndoBar from '../components/StockUndoBar';
import {
  TEAL, AMBER, RED, BORD, CARD, SUNK, DARK, MUTED, DIM, TEXT, KINDS, kindOf, num, qty, packs, day, Chip, MOVES, missingLink,
} from '../stockShared';

// Stock (the director, 22 Oct 2026): everything kept, what is on hand, and
// recording what was used, received, counted or made -- the module that
// replaces his "MAKLIN Stock Tracking" Excel, for everyone, on phones, the
// company tablet and his desktop.
//
// Grouped by product, in his Excel's order, or by kind; each group folds, and
// all start folded (23 Oct 2026), opening by themselves while searching. The
// ☰ menu lists every product, then every part, A to Z, and jumps to it.
// Stocktake walks one group and saves its counts together. On hand is the
// last count plus what came after (the server works it out).

const byName = (a, b) => a.name.localeCompare(b.name, 'sl', { sensitivity: 'base' });

export default function Stock() {
  const navigate = useNavigate();
  const { toast } = useToast();
  // Operators and setters see Stock and record in and out; a director also
  // takes stock, adds items and orders the lists (the director, 28 Oct 2026).
  const manage = useAuth().can('manage');
  const phone = useWindowWidth() < 700;
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'kind' ? 'kind' : 'product';
  const show = params.get('show') || 'all';          // all | low | uncounted
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [find, setFind] = useState('');
  const [open, setOpen] = useState(() => new Set()); // group keys unfolded; none at first
  const [menu, setMenu] = useState(false);           // the ☰ list of products and parts
  const [target, setTarget] = useState(null);        // { group, item? } to scroll to
  const [flash, setFlash] = useState(null);          // `${group}|${item}` lit for a moment
  const [recording, setRecording] = useState(null);  // { item } or { item: null }
  const [adding, setAdding] = useState(false);
  const [drag, setDrag] = useState(null);            // a line being dragged: { key, ids, from, over, dy, ... }
  const dragRef = useRef(null);
  const [counting, setCounting] = useState(null);    // a group key, while taking stock
  const [counts, setCounts] = useState({});           // `${id}` or `${id}:done` -> typed figure
  const [savingCounts, setSavingCounts] = useState(false);
  const [undo, setUndo] = useState(null);           // { ids, text }: what was just recorded, undoable for 10 s

  const load = useCallback(() => {
    api.get('/api/v1/stock')
      .then(r => { setData(r.data); setError(null); })
      .catch(err => setError(err.response?.data?.error || 'Could not read the stock. Check the connection and reload.'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setParam = (k, v, dflt) => setParams(p => { const n = new URLSearchParams(p); if (v === dflt) n.delete(k); else n.set(k, v); return n; }, { replace: true });

  const byId = useMemo(() => new Map((data?.items || []).map(i => [i.id, i])), [data]);
  const low = (data?.items || []).filter(i => i.below_min).length;
  const uncounted = (data?.items || []).filter(i => !i.level.counted).length;
  // What is left to finish (29 Oct 2026), as the server works it out.
  const unfinished = (data?.items || []).filter(i => (i.todo || []).length).length;
  const searching = find.trim() !== '';

  // What is shown: the groups, each with its items after search and filter.
  const groups = useMemo(() => {
    if (!data) return [];
    const q = find.trim().toLowerCase();
    const keep = (i) => (!q || i.name.toLowerCase().includes(q) || (i.link?.name || '').toLowerCase().includes(q))
      && (show !== 'low' || i.below_min) && (show !== 'uncounted' || !i.level.counted) && (show !== 'finishing' || (i.todo || []).length > 0);
    const base = view === 'kind'
      ? KINDS.map(k => ({ key: `k:${k.key}`, name: k.label, items: data.items.filter(i => i.kind === k.key).map(i => i.id) }))
      : data.groups;
    return base.map(g => ({ ...g, list: g.items.map(id => byId.get(id)).filter(Boolean).filter(keep) })).filter(g => g.list.length);
  }, [data, view, show, find, byId]);

  const isOpen = (key) => searching || open.has(key);
  const toggle = (key) => setOpen(s => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const allOpen = groups.length > 0 && groups.every(g => open.has(g.key));

  // Jumping from the ☰ menu: unfold the group, scroll to it, light the line.
  useEffect(() => {
    if (!target) return undefined;
    const t = setTimeout(() => {
      const el = document.querySelector(target.item != null ? `[data-jump="${target.group}|${target.item}"]` : `[data-group="${target.group}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: target.item != null ? 'center' : 'start' });
      if (target.item != null) { setFlash(`${target.group}|${target.item}`); setTimeout(() => setFlash(null), 1800); }
      setTarget(null);
    }, 60);
    return () => clearTimeout(t);
  }, [target]);
  // A jump shows everything (no search, no filter), and a product by product.
  // One change of the address, so the two settings cannot undo each other.
  const showAll = (byProduct) => setParams(p => { const n = new URLSearchParams(p); n.delete('show'); if (byProduct) n.delete('view'); return n; }, { replace: true });
  function jumpToGroup(key) {
    setMenu(false); setFind(''); showAll(true);
    setOpen(s => new Set(s).add(key));
    setTarget({ group: key });
  }
  function jumpToItem(item) {
    setMenu(false); setFind(''); showAll(false);
    const key = view === 'kind' ? `k:${item.kind}`
      : item.products.length ? `p:${[...item.products].sort((a, b) => a.sort_order - b.sort_order)[0].id}` : `k:${item.kind}`;
    setOpen(s => new Set(s).add(key));
    setTarget({ group: key, item: item.id });
  }

  // Lines dragged into a new order within their list (the director, 23 Oct
  // 2026). Only where the whole list shows -- by product, no search, no
  // filter -- so the order saved is the order seen. A grip at the left of
  // each line; mouse or finger alike; the page scrolls near its edges.
  const canDrag = manage && view === 'product' && show === 'all' && !searching;
  const dragKey = drag ? drag.key : null;
  function startDrag(e, g, index) {
    if (e.button != null && e.button > 0) return;
    e.preventDefault();
    const rows = [...document.querySelectorAll(`[data-group-rows="${g.key}"] > [data-jump]`)];
    const slots = rows.map(r => { const b = r.getBoundingClientRect(); return { top: b.top + window.scrollY, h: b.height }; });
    const st = { key: g.key, ids: g.list.map(i => i.id), from: index, over: index, startY: e.clientY + window.scrollY, clientY: e.clientY, dy: 0, slots };
    dragRef.current = st;
    setDrag(st);
  }
  useEffect(() => {
    if (!dragKey) return undefined;
    const update = () => {
      const st = dragRef.current;
      if (!st) return;
      const y = st.clientY + window.scrollY;
      let over = st.slots.findIndex(s => y < s.top + s.h / 2);
      if (over === -1) over = st.slots.length;
      dragRef.current = { ...st, over, dy: y - st.startY };
      setDrag(dragRef.current);
    };
    const move = (e) => { if (dragRef.current) { dragRef.current = { ...dragRef.current, clientY: e.clientY }; update(); } };
    const bottomBar = phone ? 64 : 0;
    const edge = setInterval(() => {
      const st = dragRef.current;
      if (!st) return;
      if (st.clientY < 90) { window.scrollBy(0, -12); update(); }
      else if (st.clientY > window.innerHeight - bottomBar - 80) { window.scrollBy(0, 12); update(); }
    }, 16);
    const up = () => {
      const st = dragRef.current;
      dragRef.current = null;
      setDrag(null);
      if (!st) return;
      const to = st.over > st.from ? st.over - 1 : st.over;
      if (to === st.from) return;
      const order = [...st.ids];
      const [moved] = order.splice(st.from, 1);
      order.splice(to, 0, moved);
      setData(d => d && ({ ...d, groups: d.groups.map(g => (g.key === st.key ? { ...g, items: order } : g)) }));
      api.put('/api/v1/stock/order', { group: st.key, item_ids: order })
        .catch(err => { toast.error(err.response?.data?.error || 'The new order was not saved. Reload and drag again.'); load(); });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      clearInterval(edge);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, [dragKey, phone, toast, load]);

  const countGroup = counting ? (view === 'kind'
    ? { name: kindOf(counting.slice(2)).label, items: (data?.items || []).filter(i => `k:${i.kind}` === counting).map(i => i.id) }
    : (data?.groups || []).find(g => g.key === counting)) : null;
  const typed = Object.entries(counts).filter(([, v]) => String(v).trim() !== '');

  async function saveCounts() {
    const movements = typed.map(([k, v]) => {
      const [id, side] = k.split(':');
      const item = byId.get(Number(id));
      return { item_id: Number(id), kind: 'counted', qty: Number(String(v).replace(',', '.')), chamfered: item?.chamfer ? side === 'done' : null };
    });
    setSavingCounts(true);
    try {
      const { data: r } = await api.post('/api/v1/stock/movements', { movements });
      setUndo({ ids: r.ids || [], text: `Counted: ${r.saved} figure${r.saved === 1 ? '' : 's'} saved for ${countGroup?.name || 'the list'}.` });
      setCounting(null); setCounts({});
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'The counts were not saved. Nothing was changed; try again.');
    } finally {
      setSavingCounts(false);
    }
  }

  const btn = (primary) => ({ background: primary ? TEAL : 'transparent', color: primary ? '#0f172a' : TEAL, border: `1px solid ${primary ? TEAL : `${TEAL}88`}`,
    borderRadius: 9, padding: phone ? '10px 14px' : '8px 14px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'system-ui', whiteSpace: 'nowrap', minHeight: 40 });
  const pill = (on) => ({ background: on ? `${TEAL}22` : 'transparent', color: on ? TEAL : DIM, border: `1px solid ${on ? TEAL : BORD}`, borderRadius: 999,
    padding: '5px 12px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui', whiteSpace: 'nowrap' });

  return (
    <div style={{ padding: phone ? '14px 12px 90px' : '20px 24px 60px', fontFamily: 'system-ui, sans-serif', color: TEXT, background: DARK, minHeight: '100vh', boxSizing: 'border-box', maxWidth: 1180, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>Stock</h1>
          <div style={{ fontSize: 13, color: MUTED, marginTop: 3 }}>
            {data ? <>{data.items.length} items{low ? <> · <span style={{ color: RED }}>{low} below minimum</span></> : ''}{uncounted ? <> · <span style={{ color: AMBER }}>{uncounted} not counted yet</span></> : ''}</> : 'Reading…'}
          </div>
        </div>
        {!counting && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', position: 'relative' }}>
            <button type="button" onClick={() => setRecording({ item: null })} style={btn(true)} disabled={!data?.items.length}>
              <i className="fa-solid fa-plus-minus" style={{ marginRight: 6 }} />Record
            </button>
            {manage && (
              <button type="button" onClick={() => setCounting(groups[0]?.key || null)} style={btn(false)} disabled={!groups.length}>
                <i className="fa-solid fa-clipboard-check" style={{ marginRight: 6 }} />Stocktake
              </button>
            )}
            {manage && <button type="button" onClick={() => setAdding(true)} style={btn(false)}><i className="fa-solid fa-plus" style={{ marginRight: 6 }} />Item</button>}
            {/* Red, so it is pressed only on purpose (the director, 29 Oct 2026). */}
            {manage && <button type="button" onClick={() => navigate('/stock/switches')} style={{ ...btn(false), color: RED, borderColor: `${RED}99` }} title="Automatic stock: runs of parts sold as they are, material by weight, pallets packed, work done outside, the check when the lorry leaves -- one switch for all, or one at a time"><i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 6 }} />Automatic stock usage</button>}
            <button type="button" onClick={() => setMenu(m => !m)} aria-label="Products and parts" aria-expanded={menu} title="Products and parts, A to Z"
              style={{ ...btn(menu), padding: phone ? '10px 13px' : '8px 12px' }} disabled={!data?.items.length}>
              <i className="fa-solid fa-bars" />
            </button>
            {menu && data && (
              <JumpMenu phone={phone} data={data} onClose={() => setMenu(false)} onProduct={jumpToGroup} onItem={jumpToItem} />
            )}
          </div>
        )}
      </div>

      {error && <div style={{ color: RED, fontSize: 13.5, margin: '10px 0' }}>{error}</div>}

      {counting && countGroup && (
        <div style={{ position: 'sticky', top: phone ? 44 : 52, zIndex: 20, background: DARK, padding: '8px 0 10px', borderBottom: `1px solid ${BORD}`, marginBottom: 10 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: TEAL }}><i className="fa-solid fa-clipboard-check" style={{ marginRight: 6 }} />Stocktake</span>
            <select value={counting} onChange={e => setCounting(e.target.value)} aria-label="Which list to count"
              style={{ background: SUNK, border: `1px solid ${BORD}`, borderRadius: 9, color: TEXT, padding: '8px 10px', fontSize: 14, fontFamily: 'system-ui', colorScheme: 'dark', maxWidth: '100%' }}>
              {(view === 'kind' ? KINDS.map(k => ({ key: `k:${k.key}`, name: k.label })) : data.groups).map(g => <option key={g.key} value={g.key}>{g.name}</option>)}
            </select>
            <span style={{ flex: 1 }} />
            <button type="button" onClick={() => { setCounting(null); setCounts({}); }} style={{ ...btn(false), color: DIM, borderColor: '#374151' }}>Cancel</button>
            <button type="button" onClick={saveCounts} disabled={!typed.length || savingCounts} style={{ ...btn(true), opacity: typed.length ? 1 : 0.5 }}>
              {savingCounts ? 'Saving…' : `Save ${typed.length || ''} count${typed.length === 1 ? '' : 's'}`.replace('  ', ' ')}
            </button>
          </div>
          <div style={{ fontSize: 12.5, color: MUTED, marginTop: 6 }}>Type what is there now. Leave an item empty if you did not count it; only typed figures are saved, dated now.</div>
        </div>
      )}

      {counting && countGroup && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {countGroup.items.map(id => byId.get(id)).filter(Boolean).map(i => (
            <div key={i.id} style={{ background: CARD, border: `1px solid ${BORD}`, borderRadius: 10, padding: '10px 12px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: phone ? 'wrap' : 'nowrap' }}>
              <div style={{ flex: phone ? '1 1 100%' : 1, minWidth: 160 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{i.name}</div>
                <div style={{ fontSize: 12, color: MUTED }}>Now {qty(i.level.total, i)}{i.level.counted ? ` · counted ${day(i.level.last_count)}` : ' · not counted yet'}</div>
              </div>
              {(i.chamfer ? [['raw', 'Not pofrezkane'], ['done', 'Pofrezkane']] : [['all', null]]).map(([side, label]) => {
                const k = side === 'all' ? String(i.id) : `${i.id}:${side}`;
                return (
                  <label key={side} style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: MUTED, width: phone ? 'calc(50% - 5px)' : 130 }}>
                    {label || i.unit}
                    <input value={counts[k] ?? ''} onChange={e => setCounts(c => ({ ...c, [k]: e.target.value }))} inputMode="decimal" placeholder={`now ${label ? num(side === 'done' ? i.level.done : i.level.raw) : num(i.level.total)}`}
                      aria-label={`Count of ${i.name}${label ? `, ${label}` : ''}`}
                      style={{ background: SUNK, border: `1px solid ${counts[k] ? TEAL : BORD}`, borderRadius: 8, color: TEXT, padding: '9px 10px', fontSize: 16, fontWeight: 700, outline: 'none', width: '100%', boxSizing: 'border-box', fontVariantNumeric: 'tabular-nums' }} />
                  </label>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {!counting && (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
            <input value={find} onChange={e => setFind(e.target.value)} placeholder="Find: Cevka, Zlozenke, Trojcek…" aria-label="Find an item"
              style={{ flex: '1 1 220px', minWidth: 0, background: SUNK, border: `1px solid ${BORD}`, borderRadius: 9, color: TEXT, padding: '9px 12px', fontSize: 14, outline: 'none', fontFamily: 'system-ui' }} />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button type="button" onClick={() => setParam('view', 'product', 'product')} style={pill(view === 'product')}>By product</button>
              <button type="button" onClick={() => setParam('view', 'kind', 'product')} style={pill(view === 'kind')}>By kind</button>
              <span style={{ width: 6 }} />
              <button type="button" onClick={() => setParam('show', 'all', 'all')} style={pill(show === 'all')}>All</button>
              <button type="button" onClick={() => setParam('show', 'low', 'all')} style={pill(show === 'low')}>Below minimum{low ? ` · ${low}` : ''}</button>
              <button type="button" onClick={() => setParam('show', 'uncounted', 'all')} style={pill(show === 'uncounted')}>Not counted yet{uncounted ? ` · ${uncounted}` : ''}</button>
              <button type="button" onClick={() => setParam('show', 'finishing', 'all')} style={pill(show === 'finishing')} title="Not counted, not linked to its Admin record, or nothing says where it goes">Needs finishing{unfinished ? ` · ${unfinished}` : ''}</button>
              {!searching && groups.length > 1 && (
                <button type="button" onClick={() => setOpen(allOpen ? new Set() : new Set(groups.map(g => g.key)))} style={pill(false)}>
                  <i className={`fa-solid fa-${allOpen ? 'compress' : 'expand'}`} style={{ marginRight: 6, fontSize: 11 }} />{allOpen ? 'Close all' : 'Open all'}
                </button>
              )}
            </div>
          </div>

          {data && !data.items.length && (
            <div style={{ background: CARD, border: `1px solid ${BORD}`, borderRadius: 12, padding: '22px 20px', color: DIM, fontSize: 14, lineHeight: 1.6 }}>
              <b style={{ color: TEXT }}>No stock items yet.</b> Add the first with <b>+ Item</b>.
            </div>
          )}
          {data && data.items.length > 0 && !groups.length && (
            <div style={{ color: MUTED, fontSize: 13.5, padding: '12px 4px' }}>Nothing matches. Clear the search or pick All.</div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {groups.map(g => {
              const unfolded = isOpen(g.key);
              const gLow = g.list.filter(i => i.below_min).length;
              const gNew = g.list.filter(i => !i.level.counted).length;
              return (
                <section key={g.key} data-group={g.key} style={{ scrollMarginTop: 70 }}>
                  <button type="button" onClick={() => toggle(g.key)} aria-expanded={unfolded}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, background: CARD, border: `1px solid ${BORD}`,
                             borderRadius: unfolded ? '12px 12px 0 0' : 12, padding: phone ? '13px 12px' : '11px 14px', cursor: 'pointer', color: TEXT, fontFamily: 'system-ui', textAlign: 'left' }}>
                    <i className={`fa-solid fa-chevron-${unfolded ? 'down' : 'right'}`} style={{ fontSize: 11, color: DIM, width: 12 }} />
                    <span style={{ fontSize: 14.5, fontWeight: 800, flex: '0 1 auto', minWidth: 0 }}>{g.name}</span>
                    <span style={{ fontSize: 12, color: MUTED, whiteSpace: 'nowrap' }}>{g.list.length} item{g.list.length === 1 ? '' : 's'}</span>
                    <span style={{ flex: 1 }} />
                    {/* How many more of the product stock would make (its BOM, 23 Oct 2026). */}
                    {g.can_make && !g.can_make.loop && (
                      <Chip color={g.can_make.n > 0 ? TEAL : AMBER} regular
                        title={g.can_make.short[0] ? `One more needs ${num(g.can_make.short[0].need)} ${g.can_make.short[0].name}; there are ${num(g.can_make.short[0].have)}.` : undefined}>
                        can make {num(g.can_make.n)}
                      </Chip>
                    )}
                    {gLow > 0 && <Chip color={RED}>{gLow} below minimum</Chip>}
                    {gNew > 0 && <Chip color={AMBER}>{gNew} not counted yet</Chip>}
                  </button>
                  {unfolded && (
                    <div data-group-rows={g.key} style={{ background: CARD, border: `1px solid ${BORD}`, borderTop: 'none', borderRadius: '0 0 12px 12px', overflow: dragKey === g.key ? 'visible' : 'hidden', userSelect: dragKey ? 'none' : undefined }}>
                      {/* How it is put together, step by step (Build steps, 27 Oct 2026). */}
                      {g.product_id && (g.list.some(i => i.kind === 'finished') || manage) && (
                        <div style={{ padding: '8px 14px', borderBottom: `1px solid ${BORD}`, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          {g.list.some(i => i.kind === 'finished') && (
                            <>
                              <Link to={`/stock/build/${g.product_id}`} style={{ color: TEAL, fontWeight: 700, textDecoration: 'none' }}>
                                <i className="fa-solid fa-diagram-project" style={{ marginRight: 6, fontSize: 11 }} />BOM
                              </Link>
                              <span style={{ color: MUTED, flex: 1, minWidth: 0 }}>what goes into {g.name}, level by level, and how it is built</span>
                            </>
                          )}
                          {/* Something new in this product (29 Oct 2026): the product picked. */}
                          {manage && (
                            <button type="button" onClick={() => setAdding({ product_id: g.product_id, product_name: g.name })}
                              style={{ marginLeft: 'auto', background: 'none', border: `1px solid ${TEAL}88`, color: TEAL, borderRadius: 8, padding: '4px 10px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'system-ui' }}>
                              <i className="fa-solid fa-plus" style={{ marginRight: 5, fontSize: 10 }} />Item
                            </button>
                          )}
                        </div>
                      )}
                      {g.list.map((i, n) => {
                        const here = drag && drag.key === g.key;
                        // Where it would land: a teal line above that line, or below the last.
                        const line = here && n !== drag.from && drag.over !== drag.from && drag.over !== drag.from + 1
                          ? (drag.over === n ? 'top' : drag.over === g.list.length && n === g.list.length - 1 ? 'bottom' : null) : null;
                        return (
                          <Row key={i.id} i={i} phone={phone} view={view} group={g} lit={flash === `${g.key}|${i.id}`}
                              grip={canDrag && g.list.length > 1 ? (e) => startDrag(e, g, n) : null}
                              dy={here && drag.from === n ? drag.dy : null} line={line}
                              onOpen={() => navigate(`/stock/${i.id}`)} onRecord={() => setRecording({ item: i })} />
                        );
                      })}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        </>
      )}

      {recording && (
        <StockRecord item={recording.item} items={data?.items || []} onClose={() => setRecording(null)}
          onSaved={(levels, m, ids) => {
            setRecording(null);
            setUndo({ ids, text: `${m.item.name}: ${m.kind === 'chamfered' ? 'made pofrezkane' : (MOVES[m.kind]?.label || m.kind).toLowerCase()} ${qty(m.qty, m.item)}.${levels[m.item.id] ? ` Now ${qty(levels[m.item.id].total, m.item)}.` : ''}` });
            load();
          }} />
      )}
      {undo && undo.ids.length > 0 && (
        <StockUndoBar key={undo.ids.join(',')} text={undo.text} ids={undo.ids} onClose={() => setUndo(null)} onUndone={() => load()} />
      )}
      {adding && (
        <StockItemForm start={adding === true ? null : adding} onClose={() => setAdding(false)} onOpenItem={(id) => { setAdding(false); navigate(`/stock/${id}`); }}
          onSaved={(it) => {
            const stay = adding !== true;
            setAdding(false);
            toast.success(stay ? `${it.name} is in ${adding.product_name}.` : `${it.name} added.`);
            if (stay) load(); else navigate(`/stock/${it.id}`);
          }} />
      )}
    </div>
  );
}

// One item in the list: its name and tags, what is on hand, its minimum and
// when it was last counted; tap for its page, or record straight away. On a
// laptop the tags sit beside the name (23 Oct 2026); on a phone, under it.
//   grip: starts dragging the line (null where the list cannot be reordered)
//   dy: how far the line being dragged has moved   line: 'top' | 'bottom' -- where it would land
function Row({ i, phone, view, group, lit, grip, dy = null, line = null, onOpen, onRecord }) {
  const k = kindOf(i.kind);
  const lv = i.level;
  const others = view === 'product' ? i.products.filter(p => `p:${p.id}` !== group.key) : [];
  const miss = missingLink(i);
  const tags = (
    <>
      <Chip color={k.color} regular>{k.label}</Chip>
      {miss && <Chip color={AMBER} title={`Not linked, so ${miss.why}. Open it and press Change.`}>not linked to {miss.to}</Chip>}
      {(i.todo || []).includes('say where it goes') && <Chip color={AMBER} title="Nothing takes it and nothing says it is only counted. Open it, press Change, and say where it goes.">in no build</Chip>}
      {(i.todo || []).some(t => t.startsWith('list what it is made of')) && <Chip color={AMBER} title="Its Build steps say nothing it is made of yet.">no list yet</Chip>}
      {!lv.counted && <Chip color={AMBER}>not counted yet</Chip>}
      {i.below_min && <Chip color={RED}>below {num(i.min_qty)}</Chip>}
    </>
  );
  const details = [
    lv.counted && !phone ? `counted ${day(lv.last_count)}` : null,
    i.min_qty != null && !i.below_min && !phone ? `min ${num(i.min_qty)}` : null,
    others.length ? `also on ${others.length} other product${others.length === 1 ? '' : 's'}` : null,
  ].filter(Boolean);
  return (
    <div data-jump={`${group.key}|${i.id}`}
      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: phone ? '11px 12px' : '9px 14px', borderTop: `1px solid ${BORD}`,
               background: dy != null ? SUNK : lit ? 'rgba(20,184,166,0.14)' : 'transparent',
               transition: dy != null ? 'none' : 'background 0.6s',
               position: 'relative', zIndex: dy != null ? 5 : 'auto',
               transform: dy != null ? `translateY(${dy}px)` : undefined,
               boxShadow: dy != null ? '0 10px 28px rgba(0,0,0,0.55)' : line === 'top' ? `inset 0 3px 0 ${TEAL}` : line === 'bottom' ? `inset 0 -3px 0 ${TEAL}` : 'none' }}>
      {grip && (
        <div onPointerDown={grip} role="button" aria-label={`Drag ${i.name} to another place in the list`} title="Drag to put it in another place"
          style={{ touchAction: 'none', cursor: dy != null ? 'grabbing' : 'grab', color: MUTED, padding: phone ? '10px 6px' : '6px 4px', margin: phone ? '-10px -4px -10px -6px' : '0 -4px 0 -6px', flexShrink: 0, fontSize: 13 }}>
          <i className="fa-solid fa-grip-vertical" />
        </div>
      )}
      <div onClick={onOpen} onKeyDown={e => { if (e.key === 'Enter') onOpen(); }} style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Link to={`/stock/${i.id}`} onClick={e => e.preventDefault()} style={{ color: TEXT, textDecoration: 'none', fontSize: 14, fontWeight: 700, lineHeight: 1.3 }}>{i.name}</Link>
          {!phone && tags}
        </div>
        {(phone || details.length > 0) && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 3, fontSize: 12, color: MUTED }}>
            {phone && tags}
            {details.map((d, n) => <span key={n} title={n === details.length - 1 && others.length ? others.map(p => p.name).join(', ') : undefined}>{d}</span>)}
          </div>
        )}
      </div>
      <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: !lv.counted ? MUTED : i.below_min ? RED : lv.total < 0 ? AMBER : TEXT }}>
          {lv.counted || lv.total ? qty(lv.total, i) : '—'}
        </div>
        {i.chamfer && (lv.counted || lv.total) ? <div style={{ fontSize: 11.5, color: DIM }}>{num(lv.done)} pofrezkane · {num(lv.raw)} not</div> : null}
        {packs(lv.total, i) && (lv.counted || lv.total) ? <div style={{ fontSize: 11.5, color: DIM }}>{packs(lv.total, i)}</div> : null}
      </div>
      <button type="button" onClick={onRecord} aria-label={`Record ${i.name}`} title="Record used, received, counted…"
        style={{ flexShrink: 0, width: 40, height: 40, borderRadius: 10, background: `${TEAL}1a`, border: `1px solid ${TEAL}66`, color: TEAL, cursor: 'pointer', fontSize: 14 }}>
        <i className="fa-solid fa-plus-minus" />
      </button>
    </div>
  );
}

// The ☰ list (the director, 23 Oct 2026): every product, then every part,
// each A to Z, with a box to narrow it down. A product unfolds its group; a
// part unfolds the group it is in and lights its line.
function JumpMenu({ phone, data, onClose, onProduct, onItem }) {
  const ref = useRef(null);
  const [q, setQ] = useState('');
  useEffect(() => {
    const out = (e) => { if (ref.current && !ref.current.contains(e.target) && !e.target.closest?.('[aria-label="Products and parts"]')) onClose(); };
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('pointerdown', out);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('pointerdown', out); document.removeEventListener('keydown', esc); };
  }, [onClose]);
  const s = q.trim().toLowerCase();
  const products = data.groups.filter(g => g.key.startsWith('p:')).filter(g => !s || g.name.toLowerCase().includes(s)).sort(byName);
  const parts = data.items.filter(i => !s || i.name.toLowerCase().includes(s) || (i.link?.name || '').toLowerCase().includes(s)).sort(byName);
  // Teal and bold headings, the names under them in a regular weight -- bold
  // names were hard to read (23 Oct 2026).
  const head = { fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: TEAL, padding: '10px 10px 4px', position: 'sticky', top: 0, background: CARD };
  const line = { display: 'flex', gap: 8, alignItems: 'baseline', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderRadius: 7, padding: '7px 10px', color: TEXT, cursor: 'pointer', fontFamily: 'system-ui', fontSize: 13.5 };
  return (
    <div ref={ref} role="menu" aria-label="Products and parts"
      style={{ position: phone ? 'fixed' : 'absolute', zIndex: 1200, background: CARD, border: `1px solid ${BORD}`, borderRadius: 12, boxShadow: '0 14px 40px rgba(0,0,0,0.55)',
               display: 'flex', flexDirection: 'column', overflow: 'hidden',
               ...(phone ? { left: 12, right: 12, top: 56, bottom: 76 } : { right: 0, top: 'calc(100% + 6px)', width: 360, maxHeight: '72vh' }) }}>
      <div style={{ padding: 8, borderBottom: `1px solid ${BORD}` }}>
        <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Find a product or part" aria-label="Find a product or part"
          style={{ width: '100%', boxSizing: 'border-box', background: SUNK, border: `1px solid ${BORD}`, borderRadius: 8, color: TEXT, padding: '8px 10px', fontSize: 14, outline: 'none', fontFamily: 'system-ui' }} />
      </div>
      <div style={{ overflowY: 'auto', padding: '0 4px 6px' }}>
        <div style={head}>Products · {products.length}</div>
        {products.map(g => (
          <button key={g.key} type="button" role="menuitem" onClick={() => onProduct(g.key)} style={line}
            onMouseEnter={e => { e.currentTarget.style.background = SUNK; }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
            <span style={{ flex: 1 }}>{g.name}</span><span style={{ fontSize: 12, color: MUTED }}>{g.items.length}</span>
          </button>
        ))}
        {!products.length && <div style={{ color: MUTED, fontSize: 12.5, padding: '4px 10px' }}>No product called that.</div>}
        <div style={head}>Parts · {parts.length}</div>
        {parts.map(i => (
          <button key={i.id} type="button" role="menuitem" onClick={() => onItem(i)} style={line}
            onMouseEnter={e => { e.currentTarget.style.background = SUNK; }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
            <span style={{ flex: 1, minWidth: 0 }}>{i.name}</span>
            <span style={{ fontSize: 12, color: MUTED, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{i.level.counted || i.level.total ? qty(i.level.total, i) : '—'}</span>
          </button>
        ))}
        {!parts.length && <div style={{ color: MUTED, fontSize: 12.5, padding: '4px 10px' }}>No part called that.</div>}
      </div>
    </div>
  );
}
